import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import socketService from '../../services/socketService';

const NotificationDebugger = () => {
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastNotification, setLastNotification] = useState(null);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const user = useSelector((state) => state.auth.user);
  const isSocketConnected = useSelector((state) => state.notification?.isSocketConnected || false);

  useEffect(() => {
    const socket = socketService.getSocket();
    
    if (!socket) {
      setConnectionStatus('not initialized');
      return;
    }

    // Check initial connection status
    setConnectionStatus(socket.connected ? 'connected' : 'disconnected');

    // Listen for connection events
    const handleConnect = () => {
      setConnectionStatus('connected');
      console.log('🔌 Socket connected in debugger');
    };

    const handleDisconnect = () => {
      setConnectionStatus('disconnected');
      console.log('🔌 Socket disconnected in debugger');
    };

    // Listen for payment notifications
    const handlePaymentVerified = (data) => {
      console.log('🔔 Payment verified notification received:', data);
      setLastNotification({ type: 'payment:verified', data, timestamp: new Date() });
      setNotificationHistory(prev => [
        { type: 'payment:verified', data, timestamp: new Date() },
        ...prev.slice(0, 9) // Keep last 10
      ]);
    };

    const handlePaymentRejected = (data) => {
      console.log('🔔 Payment rejected notification received:', data);
      setLastNotification({ type: 'payment:rejected', data, timestamp: new Date() });
      setNotificationHistory(prev => [
        { type: 'payment:rejected', data, timestamp: new Date() },
        ...prev.slice(0, 9)
      ]);
    };

    const handleUpgradeRequest = (data) => {
      console.log('🔔 Upgrade request notification received:', data);
      setLastNotification({ type: 'payment:upgrade-request', data, timestamp: new Date() });
      setNotificationHistory(prev => [
        { type: 'payment:upgrade-request', data, timestamp: new Date() },
        ...prev.slice(0, 9)
      ]);
    };

    // Add event listeners with error handling
    try {
      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);
      socket.on('payment:verified', handlePaymentVerified);
      socket.on('payment:rejected', handlePaymentRejected);
      socket.on('payment:upgrade-request', handleUpgradeRequest);
    } catch (error) {
      console.error('Error setting up socket listeners:', error);
      setConnectionStatus('error');
    }

    // Cleanup
    return () => {
      try {
        socket.off('connect', handleConnect);
        socket.off('disconnect', handleDisconnect);
        socket.off('payment:verified', handlePaymentVerified);
        socket.off('payment:rejected', handlePaymentRejected);
        socket.off('payment:upgrade-request', handleUpgradeRequest);
      } catch (error) {
        console.error('Error cleaning up socket listeners:', error);
      }
    };
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'text-green-600';
      case 'disconnected': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const testNotification = () => {
    // Simulate a test notification
    const testData = {
      type: 'success',
      title: 'Test Notification',
      message: 'This is a test notification to verify the system is working',
      timestamp: new Date().toISOString(),
      planName: 'Test Plan',
      amount: 99.99,
      currency: 'USD'
    };
    
    setLastNotification({ type: 'test', data: testData, timestamp: new Date() });
    setNotificationHistory(prev => [
      { type: 'test', data: testData, timestamp: new Date() },
      ...prev.slice(0, 9)
    ]);
  };

  // Only show for super admins or in development
  if (user?.role !== 'super_admin' && process.env.NODE_ENV === 'production') {
    return null;
  }

  return (
    <div className="bg-gray-900 text-white p-4 rounded-lg mb-6">
      <h3 className="text-lg font-bold mb-4">🔧 Notification Debugger</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-gray-800 p-3 rounded">
          <h4 className="font-semibold mb-2">Connection Status</h4>
          <p className={`font-mono ${getStatusColor(connectionStatus)}`}>
            {connectionStatus}
          </p>
          <p className="text-sm text-gray-400 mt-1">
            Redux State: {isSocketConnected ? 'connected' : 'disconnected'}
          </p>
        </div>
        
        <div className="bg-gray-800 p-3 rounded">
          <h4 className="font-semibold mb-2">User Info</h4>
          <p className="text-sm text-gray-300">
            Role: {user?.role || 'unknown'}
          </p>
          <p className="text-sm text-gray-300">
            ID: {user?._id || 'unknown'}
          </p>
        </div>
        
        <div className="bg-gray-800 p-3 rounded">
          <h4 className="font-semibold mb-2">Actions</h4>
          <button
            onClick={testNotification}
            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
          >
            Test Notification
          </button>
        </div>
      </div>

      {lastNotification && (
        <div className="bg-gray-800 p-3 rounded mb-4">
          <h4 className="font-semibold mb-2">Last Notification</h4>
          <div className="text-sm">
            <p><span className="text-blue-400">Type:</span> {lastNotification.type}</p>
            <p><span className="text-blue-400">Time:</span> {lastNotification.timestamp.toLocaleTimeString()}</p>
            <p><span className="text-blue-400">Title:</span> {lastNotification.data.title}</p>
            <p><span className="text-blue-400">Message:</span> {lastNotification.data.message}</p>
          </div>
        </div>
      )}

      {notificationHistory.length > 0 && (
        <div className="bg-gray-800 p-3 rounded">
          <h4 className="font-semibold mb-2">Notification History</h4>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {notificationHistory.map((notification, index) => (
              <div key={index} className="text-xs border-b border-gray-700 pb-1">
                <span className="text-blue-400">{notification.type}</span>
                <span className="text-gray-400 ml-2">
                  {notification.timestamp.toLocaleTimeString()}
                </span>
                <p className="text-gray-300 truncate">{notification.data.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDebugger;